import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Spin, Tag, Pagination } from 'antd';
import {
  AppstoreOutlined,
  BranchesOutlined,
  CarryOutOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  RobotOutlined,
  AuditOutlined,
  ProfileOutlined
} from '@ant-design/icons';
import { dashboardApi } from '../../services/api';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import dayjs from 'dayjs';

/**
 * 工作台首页
 * 
 * ============================================
 * 后端接口拆分设计
 * ============================================
 * 
 * 接口1: GET /api/dashboard/overview-stats
 * 描述: 获取核心概览统计（项目空间、业务场景、用例总数）
 * 响应数据格式:
 * {
 *   totalDomains: number,      // 项目空间总数
 *   totalScenarios: number,    // 业务场景总数
 *   totalUseCases: number      // 用例总数
 * }
 * 
 * 接口2: GET /api/dashboard/task-stats
 * 描述: 获取任务执行统计（评测任务、Benchmark相关数据）
 * 响应数据格式:
 * {
 *   totalTasks: number,         // 评测任务总数
 *   totalBenchmarks: number,    // Benchmark总数
 *   runningTasks: number,       // 执行中任务数
 *   completedTasks: number,     // 成功任务数
 *   failedTasks: number,        // 失败任务数
 *   pendingTasks: number,       // 待执行任务数
 *   successRate: number,        // 整体成功率(百分比,如 92.5)
 *   avgExecutionTime: number    // 平均执行时长(秒)
 * }
 * 
 * 接口3: GET /api/dashboard/task-status-distribution
 * 描述: 获取任务状态分布（用于饼图展示）
 * 响应数据格式:
 * {
 *   distribution: [
 *     { status: '执行成功', count: number, color: '#22c55e' },
 *     { status: '执行中', count: number, color: '#f59e0b' },
 *     { status: '执行失败', count: number, color: '#ef4444' },
 *     { status: '待执行', count: number, color: '#94a3b8' }
 *   ]
 * }
 * 
 * 接口4: GET /api/dashboard/domain-radar
 * 描述: 获取项目空间能力分析（雷达图数据）
 * 响应数据格式:
 * {
 *   radarData: [
 *     {
 *       domain: string,        // 项目空间名称
 *       scenarios: number,     // 场景数(0-100归一化)
 *       tasks: number,         // 任务数(0-100归一化)
 *       success: number,       // 成功率(0-100)
 *       benchmarks: number     // Benchmark数(0-100归一化)
 *     }
 *   ]
 * }
 * 
 * 接口5: GET /api/dashboard/case-execution-stats
 * 描述: 获取用例执行详情统计（断言通过/不通过/异常）
 * 响应数据格式:
 * {
 *   executionStats: [
 *     { 
 *       status: '断言通过', 
 *       count: number, 
 *       color: '#22c55e', 
 *       percentage: number,
 *       relatedScenarios: string[]  // 关联场景列表（支持多个）
 *     },
 *     { status: '断言不通过', count: number, color: '#ef4444', percentage: number, relatedScenarios: string[] },
 *     { status: '执行异常', count: number, color: '#f59e0b', percentage: number, relatedScenarios: string[] }
 *   ],
 *   passRate: number           // 用例通过率(百分比)
 * }
 * 
 * 接口6: GET /api/dashboard/benchmark-evolution
 * 描述: 获取Benchmark版本演进列表（支持分页）
 * 请求参数: 
 *   - pageNo?: number (可选，页码，默认1)
 *   - pageSize?: number (可选，每页条数，默认5)
 * 响应数据格式:
 * {
 *   total: number,              // 总条数
 *   benchmarks: [
 *     {
 *       id: number,
 *       benchmarkVersion: string,     // 版本号
 *       benchmarkType: string,        // 类型: '基线benchmark'|'样本验收benchmark'|'线上周期评测benchmark'
 *       businessScenarioName: string, // 业务场景名称
 *       useCaseCount: number,         // 用例数量
 *       gmtCreate: string             // 创建时间 YYYY-MM-DD HH:mm:ss
 *     }
 *   ]
 * }
 * 
 * 接口7: GET /api/dashboard/scenario-category-stats
 * 描述: 获取业务场景分类统计数据
 * 响应数据格式:
 * {
 *   totalScenarios: number,        // 业务场景总数
 *   categoryStats: [
 *     {
 *       category: string,          // 分类名称: '要素抽取'|'机审'|'机器人问答'
 *       categoryKey: string,       // 分类标识: 'FEATURE_EXTRACTION'|'AUTO_REVIEW'|'ROBOT_QA'
 *       scenarioCount: number,     // 该分类下场景数量
 *       judgeSetCount: number,     // 评测集数量
 *       useCaseCount: number,      // 用例数量
 *       taskCount: number,         // 评测任务数量
 *       passRate: number           // 用例通过率(百分比)
 *     }
 *   ]
 * }
 * ============================================
 */
function Dashboard() {
  // 区块1: 核心概览统计 loading 状态
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewStats, setOverviewStats] = useState({
    totalDomains: 0,
    totalScenarios: 0,
    totalUseCases: 0
  });

  // 区块2: 任务执行统计 loading 状态
  const [taskStatsLoading, setTaskStatsLoading] = useState(true);
  const [taskStats, setTaskStats] = useState({
    totalTasks: 0,
    totalBenchmarks: 0,
    runningTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    pendingTasks: 0,
    successRate: 0,
    avgExecutionTime: 0
  });

  // 区块3: 任务状态分布 loading 状态
  const [taskDistributionLoading, setTaskDistributionLoading] = useState(true);
  const [taskStatusDistribution, setTaskStatusDistribution] = useState([]);

  // 区块4: 项目空间能力分析 loading 状态
  const [radarLoading, setRadarLoading] = useState(true);
  const [radarData, setRadarData] = useState([]);

  // 区块5: 用例执行详情 loading 状态
  const [caseExecutionLoading, setCaseExecutionLoading] = useState(true);
  const [caseExecutionData, setCaseExecutionData] = useState([]);
  const [casesPassRate, setCasesPassRate] = useState(0);

  // 区块6: Benchmark版本演进 loading 状态
  const [benchmarkLoading, setBenchmarkLoading] = useState(true);
  const [benchmarkList, setBenchmarkList] = useState([]);
  const [benchmarkTotal, setBenchmarkTotal] = useState(0);
  const [benchmarkPage, setBenchmarkPage] = useState(1);
  const [benchmarkPageSize] = useState(5);

  // 区块7: 业务场景分类统计 loading 状态
  const [scenarioStatsLoading, setScenarioStatsLoading] = useState(true);
  const [scenarioCategoryStats, setScenarioCategoryStats] = useState({
    totalScenarios: 0,
    categoryStats: []
  });

  // Mock 数据（接口未实现时使用）
  const mockRadarData = [
    { domain: '合同签章', scenarios: 90, tasks: 85, success: 96, benchmarks: 88 },
    { domain: '合规审查', scenarios: 75, tasks: 70, success: 95, benchmarks: 75 },
    { domain: '维权诉讼', scenarios: 65, tasks: 60, success: 92, benchmarks: 65 },
    { domain: '无形资产', scenarios: 55, tasks: 50, success: 94, benchmarks: 58 },
    { domain: 'ESG', scenarios: 45, tasks: 40, success: 91, benchmarks: 48 }
  ];

  const mockCaseExecutionData = [
    { status: '断言通过', count: 1160, color: '#22c55e', percentage: 92.8, relatedScenarios: ['合同机审', '要素抽取'] },
    { status: '断言不通过', count: 65, color: '#ef4444', percentage: 5.2, relatedScenarios: ['要素抽取', '问答场景'] },
    { status: '执行异常', count: 25, color: '#f59e0b', percentage: 2.0, relatedScenarios: ['问答场景'] }
  ];

  const mockBenchmarkData = [
    { id: 1, benchmarkVersion: 'V202401221200', benchmarkType: 'BASELINE', businessScenarioName: '合同机审-类模板审查', useCaseCount: 150, gmtCreate: '2024-01-22 12:00:00' },
    { id: 2, benchmarkVersion: 'V202401211500', benchmarkType: 'ACCEPTANCE', businessScenarioName: '合同机审-通用文本审查', useCaseCount: 200, gmtCreate: '2024-01-21 15:00:00' },
    { id: 3, benchmarkVersion: 'V202401201000', benchmarkType: 'PERIODIC', businessScenarioName: '要素抽取-合同关键信息', useCaseCount: 120, gmtCreate: '2024-01-20 10:00:00' },
    { id: 4, benchmarkVersion: 'V202401191400', benchmarkType: 'BASELINE', businessScenarioName: '文件机审-证照文件审查', useCaseCount: 180, gmtCreate: '2024-01-19 14:00:00' },
    { id: 5, benchmarkVersion: 'V202401180900', benchmarkType: 'ACCEPTANCE', businessScenarioName: '合同机审-类模板审查', useCaseCount: 160, gmtCreate: '2024-01-18 09:00:00' },
    { id: 6, benchmarkVersion: 'V202401171600', benchmarkType: 'BASELINE', businessScenarioName: '合同机审-通用文本审查', useCaseCount: 140, gmtCreate: '2024-01-17 16:00:00' }
  ];

  // Mock 业务场景分类统计数据
  const mockScenarioCategoryStats = {
    totalScenarios: 28,
    categoryStats: [
      {
        category: '要素抽取',
        categoryKey: 'FEATURE_EXTRACTION',
        scenarioCount: 12,
        judgeSetCount: 45,
        useCaseCount: 3200,
        taskCount: 156,
        passRate: 94.5,
        color: '#3b82f6',
        icon: 'ProfileOutlined'
      },
      {
        category: '机审',
        categoryKey: 'AUTO_REVIEW',
        scenarioCount: 10,
        judgeSetCount: 38,
        useCaseCount: 2800,
        taskCount: 132,
        passRate: 91.2,
        color: '#10b981',
        icon: 'AuditOutlined'
      },
      {
        category: '机器人问答',
        categoryKey: 'ROBOT_QA',
        scenarioCount: 6,
        judgeSetCount: 22,
        useCaseCount: 1500,
        taskCount: 78,
        passRate: 88.7,
        color: '#8b5cf6',
        icon: 'RobotOutlined'
      }
    ]
  };

  useEffect(() => {
    // 并行请求各区块数据
    fetchOverviewStats();
    fetchTaskStats();
    fetchTaskStatusDistribution();
    fetchDomainRadar();
    fetchCaseExecutionStats();
    fetchScenarioCategoryStats();
  }, []);

  // Benchmark分页独立请求
  useEffect(() => {
    fetchBenchmarkEvolution();
  }, [benchmarkPage]);

  /**
   * 接口1: 获取核心概览统计
   * GET /api/dashboard/overview-stats
   */
  const fetchOverviewStats = async () => {
    setOverviewLoading(true);
    try {
      const res = await dashboardApi.getOverviewStats();
      setOverviewStats({
        totalDomains: res?.totalDomains || 0,
        totalScenarios: res?.totalScenarios || 0,
        totalUseCases: res?.totalUseCases || 0
      });
    } catch (error) {
      console.error('获取核心概览统计失败:', error);
    } finally {
      setOverviewLoading(false);
    }
  };

  /**
   * 接口2: 获取任务执行统计
   * GET /api/dashboard/task-stats
   */
  const fetchTaskStats = async () => {
    setTaskStatsLoading(true);
    try {
      const res = await dashboardApi.getTaskStats();
      setTaskStats({
        totalTasks: res?.totalTasks || 0,
        totalBenchmarks: res?.totalBenchmarks || 0,
        runningTasks: res?.runningTasks || 0,
        completedTasks: res?.completedTasks || 0,
        failedTasks: res?.failedTasks || 0,
        pendingTasks: res?.pendingTasks || 0,
        successRate: res?.successRate || 0,
        avgExecutionTime: res?.avgExecutionTime || 0
      });
    } catch (error) {
      console.error('获取任务执行统计失败:', error);
    } finally {
      setTaskStatsLoading(false);
    }
  };

  /**
   * 接口3: 获取任务状态分布
   * GET /api/dashboard/task-status-distribution
   */
  const fetchTaskStatusDistribution = async () => {
    setTaskDistributionLoading(true);
    try {
      const res = await dashboardApi.getTaskStatusDistribution();
      const distribution = res?.distribution || [];
      setTaskStatusDistribution(distribution.filter(item => item.count > 0));
    } catch (error) {
      console.error('获取任务状态分布失败:', error);
      // 使用 taskStats 数据生成默认分布
    } finally {
      setTaskDistributionLoading(false);
    }
  };

  /**
   * 接口4: 获取项目空间能力分析
   * GET /api/dashboard/domain-radar
   */
  const fetchDomainRadar = async () => {
    setRadarLoading(true);
    try {
      const res = await dashboardApi.getDomainRadar();
      setRadarData(res?.radarData || mockRadarData);
    } catch (error) {
      console.error('获取项目空间能力分析失败:', error);
      setRadarData(mockRadarData);
    } finally {
      setRadarLoading(false);
    }
  };

  /**
   * 接口5: 获取用例执行详情统计
   * GET /api/dashboard/case-execution-stats
   */
  const fetchCaseExecutionStats = async () => {
    setCaseExecutionLoading(true);
    try {
      const res = await dashboardApi.getCaseExecutionStats();
      setCaseExecutionData(res?.executionStats || mockCaseExecutionData);
      setCasesPassRate(res?.passRate || 92.8);
    } catch (error) {
      console.error('获取用例执行详情统计失败:', error);
      setCaseExecutionData(mockCaseExecutionData);
      setCasesPassRate(92.8);
    } finally {
      setCaseExecutionLoading(false);
    }
  };

  /**
   * 接口6: 获取Benchmark版本演进列表（支持分页）
   * GET /api/dashboard/benchmark-evolution
   */
  const fetchBenchmarkEvolution = async () => {
    setBenchmarkLoading(true);
    try {
      const res = await dashboardApi.getBenchmarkEvolution({ 
        pageNo: benchmarkPage, 
        pageSize: benchmarkPageSize 
      });
      setBenchmarkList(res?.benchmarks || mockBenchmarkData);
      setBenchmarkTotal(res?.total || mockBenchmarkData.length);
    } catch (error) {
      console.error('获取Benchmark版本演进失败:', error);
      setBenchmarkList(mockBenchmarkData);
      setBenchmarkTotal(mockBenchmarkData.length);
    } finally {
      setBenchmarkLoading(false);
    }
  };

  // Benchmark分页切换
  const handleBenchmarkPageChange = (page) => {
    setBenchmarkPage(page);
  };

  /**
   * 接口7: 获取业务场景分类统计
   * GET /api/dashboard/scenario-category-stats
   */
  const fetchScenarioCategoryStats = async () => {
    setScenarioStatsLoading(true);
    try {
      const res = await dashboardApi.getScenarioCategoryStats();
      setScenarioCategoryStats({
        totalScenarios: res?.totalScenarios || mockScenarioCategoryStats.totalScenarios,
        categoryStats: res?.categoryStats || mockScenarioCategoryStats.categoryStats
      });
    } catch (error) {
      console.error('获取业务场景分类统计失败:', error);
      setScenarioCategoryStats(mockScenarioCategoryStats);
    } finally {
      setScenarioStatsLoading(false);
    }
  };

  // 获取分类图标组件
  const getCategoryIcon = (iconName) => {
    const iconMap = {
      'ProfileOutlined': <ProfileOutlined className="text-2xl" />,
      'AuditOutlined': <AuditOutlined className="text-2xl" />,
      'RobotOutlined': <RobotOutlined className="text-2xl" />
    };
    return iconMap[iconName] || <BranchesOutlined className="text-2xl" />;
  };

  // 整体成功率（优先使用接口返回，否则计算）
  const successRate = useMemo(() => {
    const rate = typeof taskStats.successRate === 'string' 
      ? parseFloat(taskStats.successRate) 
      : taskStats.successRate;
    if (!isNaN(rate) && rate > 0) {
      return rate.toFixed(1);
    }
    const total = taskStats.completedTasks + taskStats.failedTasks;
    if (total === 0) return '0.0';
    return ((taskStats.completedTasks / total) * 100).toFixed(1);
  }, [taskStats]);

  // 任务状态分布饼图数据（优先使用接口返回，否则根据 taskStats 生成）
  const taskStatusData = useMemo(() => {
    if (taskStatusDistribution.length > 0) {
      return taskStatusDistribution;
    }
    return [
      { status: '执行成功', count: taskStats.completedTasks, color: '#22c55e' },
      { status: '执行中', count: taskStats.runningTasks, color: '#f59e0b' },
      { status: '执行失败', count: taskStats.failedTasks, color: '#ef4444' },
      { status: '待执行', count: taskStats.pendingTasks, color: '#94a3b8' }
    ].filter(item => item.count > 0);
  }, [taskStatusDistribution, taskStats]);

  const displayBenchmarks = benchmarkList;

  return (
    <div className="space-y-6 p-6 bg-[#f5f7fa] min-h-screen">
      {/* 核心指标概览 - 接口1: /api/dashboard/overview-stats */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-10 mb-8 border border-blue-100">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-800 mb-3">ALT 智能化评测平台 · 数据概览</h2>
          <p className="text-lg text-gray-600">全面掌握系统运行状态和关键性能指标</p>
        </div>

        {/* 核心业务指标 - 三列渐变卡片 */}
        <Spin spinning={overviewLoading}>
          <Row gutter={[32, 32]} className="mb-8">
            <Col xs={24} sm={8}>
              <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-2xl p-8 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 h-40 flex items-center">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-5xl font-black mb-2 tracking-tight">{overviewStats.totalDomains}</div>
                    <div className="text-blue-100 text-lg font-medium">项目空间总数</div>
                    <div className="text-blue-200 text-sm mt-1">Business Domains</div>
                  </div>
                  <div className="text-6xl opacity-90">
                    <AppstoreOutlined />
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <div className="bg-gradient-to-br from-green-500 via-green-600 to-green-700 rounded-2xl p-8 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 h-40 flex items-center">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-5xl font-black mb-2 tracking-tight">{overviewStats.totalScenarios}</div>
                    <div className="text-green-100 text-lg font-medium">业务场景总数</div>
                    <div className="text-green-200 text-sm mt-1">Business Scenarios</div>
                  </div>
                  <div className="text-6xl opacity-90">
                    <BranchesOutlined />
                  </div>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-purple-700 rounded-2xl p-8 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 h-40 flex items-center">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="text-5xl font-black mb-2 tracking-tight">{overviewStats.totalUseCases}</div>
                    <div className="text-purple-100 text-lg font-medium">用例总数</div>
                    <div className="text-purple-200 text-sm mt-1">Total Cases</div>
                  </div>
                  <div className="text-6xl opacity-90">
                    <FileTextOutlined />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Spin>

        {/* 执行统计指标 - 三列卡片 - 接口2: /api/dashboard/task-stats */}
        <Spin spinning={taskStatsLoading}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8}>
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-200 hover:border-orange-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-48">
                <div className="text-center h-full flex flex-col justify-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
                    <CarryOutOutlined className="text-2xl text-orange-600" />
                  </div>
                  <div className="text-3xl font-black text-orange-600 mb-2">{taskStats.totalTasks}</div>
                  <div className="text-gray-700 text-sm font-semibold">评测任务总数</div>
                  <div className="text-gray-500 text-xs mt-1">Total Tasks</div>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-indigo-200 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-48">
                <div className="text-center h-full flex flex-col justify-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-indigo-100 rounded-full flex items-center justify-center">
                    <TrophyOutlined className="text-2xl text-indigo-600" />
                  </div>
                  <div className="text-3xl font-black text-indigo-600 mb-2">{taskStats.totalBenchmarks}</div>
                  <div className="text-gray-700 text-sm font-semibold">Benchmark总数</div>
                  <div className="text-gray-500 text-xs mt-1">Total Benchmarks</div>
                </div>
              </div>
            </Col>

            <Col xs={24} sm={8}>
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-emerald-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-48">
                <div className="text-center h-full flex flex-col justify-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-emerald-100 rounded-full flex items-center justify-center">
                    <FileTextOutlined className="text-2xl text-emerald-600" />
                  </div>
                  <div className="text-3xl font-black text-emerald-600 mb-2">{casesPassRate}%</div>
                  <div className="text-gray-700 text-sm font-semibold">用例通过率</div>
                  <div className="text-gray-500 text-xs mt-1">Pass Rate</div>
                </div>
              </div>
            </Col>
          </Row>
        </Spin>
      </div>

      {/* 项目空间统计分析 - 接口4: /api/dashboard/domain-radar */}
      <div className="bg-white rounded-xl p-8 mb-8 shadow-lg border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <AppstoreOutlined className="mr-3 text-blue-600" />
          项目空间统计分析
        </h3>
        <Spin spinning={radarLoading}>
          <ResponsiveContainer width="100%" height={450}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="domain" tick={{ fontSize: 12, fill: '#666' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#666' }} />
              <Radar name="场景数" dataKey="scenarios" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} />
              <Radar name="任务数" dataKey="tasks" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} />
              <Radar name="成功率" dataKey="success" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} />
              <Radar name="Benchmark数" dataKey="benchmarks" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </Spin>
      </div>

      {/* 业务场景统计 - 接口7: /api/dashboard/scenario-category-stats */}
      <div className="bg-white rounded-xl p-8 mb-8 shadow-lg border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <BranchesOutlined className="mr-3 text-green-600" />
          业务场景统计
        </h3>
        
        <Spin spinning={scenarioStatsLoading}>
          {/* 场景总数概览 */}
          <div className="mb-8">
            <div className="text-center p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <div className="text-5xl font-black text-green-600 mb-2">{scenarioCategoryStats.totalScenarios}</div>
              <div className="text-gray-700 text-lg font-medium">业务场景总数</div>
              <div className="text-gray-500 text-sm mt-1">包含要素抽取、机审、机器人问答三类场景</div>
            </div>
          </div>

          {/* 分类统计卡片 */}
          <Row gutter={[24, 24]} className="mb-8">
            {scenarioCategoryStats.categoryStats.map((item, index) => (
              <Col xs={24} md={8} key={index}>
                <div 
                  className="rounded-2xl p-6 border-2 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full"
                  style={{ borderColor: item.color, backgroundColor: `${item.color}08` }}
                >
                  <div className="flex items-center mb-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center mr-4"
                      style={{ backgroundColor: `${item.color}20`, color: item.color }}
                    >
                      {getCategoryIcon(item.icon)}
                    </div>
                    <div>
                      <div className="text-xl font-bold text-gray-800">{item.category}</div>
                      <div className="text-sm text-gray-500">{item.scenarioCount} 个场景</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <div className="text-2xl font-bold" style={{ color: item.color }}>{item.judgeSetCount}</div>
                      <div className="text-xs text-gray-500">评测集</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <div className="text-2xl font-bold" style={{ color: item.color }}>{item.useCaseCount}</div>
                      <div className="text-xs text-gray-500">用例数</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <div className="text-2xl font-bold" style={{ color: item.color }}>{item.taskCount}</div>
                      <div className="text-xs text-gray-500">评测任务</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-gray-100">
                      <div className="text-2xl font-bold text-green-600">{item.passRate}%</div>
                      <div className="text-xs text-gray-500">通过率</div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* 分类指标对比图表 */}
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-gray-700 mb-4">分类指标对比</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={scenarioCategoryStats.categoryStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#666' }} />
                <YAxis dataKey="category" type="category" tick={{ fontSize: 12, fill: '#666' }} width={80} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="useCaseCount" name="用例数" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="taskCount" name="任务数" fill="#10b981" radius={[0, 4, 4, 0]} />
                <Bar dataKey="judgeSetCount" name="评测集" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Spin>
      </div>

      {/* 评测任务执行统计 - 接口3: /api/dashboard/task-status-distribution */}
      <div className="bg-white rounded-xl p-8 mb-8 shadow-lg border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
          <CarryOutOutlined className="mr-3 text-blue-600" />
          评测任务执行统计
        </h3>

        <Spin spinning={taskDistributionLoading || taskStatsLoading}>
          {/* 关键指标卡片 */}
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8}>
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 hover:shadow-lg transition-all duration-300">
                <div className="text-4xl font-bold text-blue-600 mb-2">{taskStats.totalTasks}</div>
                <div className="text-gray-700 font-medium">任务总数</div>
                <div className="text-green-600 text-sm mt-2 flex items-center justify-center">
                  <span className="mr-1">↗</span> 12个
                </div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 hover:shadow-lg transition-all duration-300">
                <div className="text-4xl font-bold text-orange-600 mb-2">{taskStats.runningTasks}</div>
                <div className="text-gray-700 font-medium">执行中任务</div>
                <div className="text-orange-600 text-sm mt-2">当前运行</div>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200 hover:shadow-lg transition-all duration-300">
                <div className="text-4xl font-bold text-purple-600 mb-2">{taskStats.avgExecutionTime || 2.3}s</div>
                <div className="text-gray-700 font-medium">平均执行时长</div>
                <div className="text-green-600 text-sm mt-2 flex items-center justify-center">
                  <span className="mr-1">↓</span> 0.3s
                </div>
              </div>
            </Col>
          </Row>
        </Spin>
      </div>

      {/* 用例执行详情统计 - 接口5: /api/dashboard/case-execution-stats */}
      <div className="bg-white rounded-xl p-8 mb-8 shadow-lg border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
          <CheckCircleOutlined className="mr-3 text-emerald-600" />
          用例执行详情统计
        </h3>

        <Spin spinning={caseExecutionLoading}>
          <Row gutter={[32, 32]}>
            {caseExecutionData.map((item, index) => {
              // 根据索引确定文案
              const statusText = index === 0 ? '断言通过用例总数' : 
                                index === 1 ? '断言不通过用例总数' : '执行异常用例总数';
              return (
                <Col xs={24} sm={8} key={index}>
                  <div className="relative p-8 rounded-2xl border-2 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                       style={{ borderColor: item.color, backgroundColor: `${item.color}08` }}>
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                           style={{ backgroundColor: `${item.color}20` }}>
                        <div className="w-8 h-8 rounded-full" style={{ backgroundColor: item.color }} />
                      </div>
                      <div className="text-4xl font-bold mb-2" style={{ color: item.color }}>
                        {item.count}
                      </div>
                      <div className="text-lg font-semibold text-gray-800 mb-2">{statusText}</div>
                      <div className="text-2xl font-bold text-gray-600 mb-4">{item.percentage}%</div>

                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Spin>
      </div>

      {/* Benchmark版本演进详情表格 - 接口6: /api/dashboard/benchmark-evolution */}
      <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
          <TrophyOutlined className="mr-3 text-purple-600" />
          Benchmark版本演进详情
        </h3>

        <Spin spinning={benchmarkLoading}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">版本号</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">类型</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">业务场景</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">用例数量</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">创建时间</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">状态</th>
                </tr>
              </thead>
              <tbody>
                {displayBenchmarks.map((item, index) => (
                  <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span 
                          className="font-medium text-blue-600 cursor-pointer hover:text-blue-800 hover:underline"
                          onClick={() => item.id && window.open(`#/benchmark/${item.id}`, '_blank')}
                        >
                          {item.benchmarkVersion || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        (item.benchmarkType === 'BASELINE' || item.benchmarkType === '基线benchmark') ? 'bg-blue-100 text-blue-800' :
                        (item.benchmarkType === 'ACCEPTANCE' || item.benchmarkType === '样本验收benchmark') ? 'bg-green-100 text-green-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {item.benchmarkType === 'BASELINE' ? '基线benchmark' :
                         item.benchmarkType === 'ACCEPTANCE' ? '样本验收benchmark' :
                         item.benchmarkType === 'PERIODIC' ? '线上周期评测benchmark' :
                         item.benchmarkType || '-'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-700">{item.businessScenarioName || '-'}</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                        {item.useCaseCount || 0}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className="text-gray-600 text-sm">
                        {item.gmtCreate ? dayjs(item.gmtCreate).format('YYYY-MM-DD HH:mm') : '-'}
                      </span>
                    </td>
                    <td className="text-center py-4 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        index === 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {index === 0 ? '最新版本' : '历史版本'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* 分页器 */}
          {benchmarkTotal > benchmarkPageSize && (
            <div className="flex justify-end mt-6">
              <Pagination
                current={benchmarkPage}
                total={benchmarkTotal}
                pageSize={benchmarkPageSize}
                onChange={handleBenchmarkPageChange}
                showSizeChanger={false}
                showTotal={(total) => `共 ${total} 条`}
              />
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
}

export default Dashboard;
